// One-shot prefill handoff from the sales funnel (a deal, or the Gouden
// Standaard client picker) to the internal document generator.
//
// Transport split (see doc/sales/funnel-integration-design.md):
//   - the template id travels in the URL (?template=…) — non-sensitive,
//     deep-linkable;
//   - the field values (client name, signatory, aanleiding) travel via
//     localStorage so they never land in Caddy access logs, browser history,
//     or Referer headers.
//
// localStorage (not sessionStorage) because the generator opens in a NEW TAB
// with rel=noopener — a noopener context does not inherit the opener's
// sessionStorage, but localStorage is shared per-origin. The stash is consumed
// once (read-and-clear) so the client name does not linger and a stale handoff
// cannot bleed into a later, unrelated generate.

const KEY = "riskaia.offer.prefill.v1";

export const OFFER_TEMPLATE = "offer-overheid-nl";

export function stashOfferPrefill({ template, fields }) {
  try {
    window.localStorage.setItem(
      KEY,
      JSON.stringify({ template, fields: fields || {} }),
    );
  } catch {
    // localStorage unavailable — degrade gracefully to template-only
    // selection via the URL param.
  }
}

// Read + clear (one-shot). Returns the stored fields only when the stash's
// template matches the one now being generated, guarding against a stale
// stash leaking into a different document.
export function takeOfferPrefill(template) {
  let raw;
  try {
    raw = window.localStorage.getItem(KEY);
    window.localStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (template && parsed.template && parsed.template !== template) return null;
    return parsed.fields || null;
  } catch {
    return null;
  }
}

const isProvincie = (name) => /^\s*provincie\b/i.test(name);

// Map a sales deal to offer-overheid-nl field values. Only fields that map
// faithfully are set. Pricing (`posten`) is intentionally NOT derived: a deal's
// single forecast `value` is not a quote line-item breakdown, so the template
// keeps its default posten for the salesperson to complete.
export function dealToOfferFields(deal) {
  if (!deal) return {};
  const fields = {};
  const org = (deal.municipality || "").trim();
  if (org) {
    fields.recipient_name = org;
    fields.recipient_type = isProvincie(org) ? "Provincie" : "Gemeente";
  }
  const signatory = (deal.owner || deal.eigenaar || "").trim();
  if (signatory) fields.provider_signatory = signatory;
  const aanleiding = (deal.notes || deal.last_action || "").trim();
  if (aanleiding) fields.intro = aanleiding;
  return fields;
}

// Map a bare client name (from the Gouden Standaard picker) to the recipient
// field of whichever generator template is being produced.
export function clientToFields(template, client) {
  const name = (client || "").trim();
  if (!name) return {};
  if (template === OFFER_TEMPLATE) {
    return {
      recipient_name: name,
      recipient_type: isProvincie(name) ? "Provincie" : "Gemeente",
    };
  }
  if (template === "exclusivity-declaration-nl") {
    return { bestemd_voor: name };
  }
  return {};
}
